import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { supabaseUpload } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import { isUuid, setPageSeo, productUrl } from '../utils/urls';
import { optimizeImageUrl } from '../utils/imageUrl';
import { vacationStatus, formatDateIt } from '../utils/vacation';
import { calculateFinalPrice, calculateFullPrice, isLaunchModeActive, getTotalDiscountPercentage } from '../lib/feeCalculator';
import { CrafterStripeStatusBanner } from '../components/CrafterStripeStatusBanner';
import { useCrafterCanSell } from '../hooks/useCrafterCanSell';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { CrafterBadges, RarifexVerifiedBadge, Tricolore, CERTIFICATIONS } from '../components/MadeInItaly';
import { CrafterBadge } from '../components/CrafterBadge';
import { ReviewSummary, ReviewsList } from '../components/ReviewsList';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  MapPin, 
  Star, 
  Package, 
  Image as ImageIcon,
  Images,
  MessageSquare,
  Crown,
  Clock,
  ChevronRight,
  Heart,
  Award,
  Languages,
  Play,
  History,
  TrendingUp,
  ShoppingBag,
  Users,
  Calendar,
  Lock,
  FileText,
  X,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  Grid3X3,
  List,
  Folder,
  ChevronDown,
  Sparkles,
  Gift,
  Upload,
  Loader2,
  Share2,
  Globe,
  Store,
  Layers
} from 'lucide-react';
import { sortProductsByAvailability, createAvailabilityComparator, getProductAvailability } from '../utils/productAvailability';
import { getChildCategoryName } from '../utils/categoryPath';
import DirectRequestDialog from '../components/DirectRequestDialog';
import { parseVideo } from '../utils/video';
import { fetchPublicShop, fetchMyShop } from '../lib/shopsApi';
import { SHOP_MENU_ITEMS, normalizeMenu, shopThemeStyle, layoutClasses } from '../lib/shopThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrafterProfilePage() {
  const params = useParams();
  const routeKey = params.slug || params.id;
  const slugFromRoute = params.slug;
  const [id, setId] = useState(isUuid(routeKey) ? routeKey : null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFavorite: checkIsFavorite, toggleFavorite } = useFavorites();
  const [crafter, setCrafter] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingChat, setStartingChat] = useState(false);
  // Stripe onboarding status del crafter (per banner "in attivazione")
  const { canSell: crafterCanSell, loading: canSellLoading } = useCrafterCanSell(crafter?.id);
  
  // Stats
  const [completedOrdersCount, setCompletedOrdersCount] = useState(0);
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, reviewCount: 0, subRatingAverages: {} });
  const [productReviews, setProductReviews] = useState({});
  
  // Products filter/sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [productCategories, setProductCategories] = useState([]);
  
  // Media gallery state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaType, setMediaType] = useState('video');
  
  // Sticky menu state
  const [activeSection, setActiveSection] = useState('intro');
  const [isMenuSticky, setIsMenuSticky] = useState(false);
  const menuRef = useRef(null);
  // Prima sezione della pagina (cambia se l'artigiano riordina il menu): evidenziata finché non si scorre
  const firstSectionRef = useRef('intro');
  const menuPlaceholderRef = useRef(null);

  // Direct request dialog state
  const [showDirectRequestDialog, setShowDirectRequestDialog] = useState(false);


  // Favorites - check if this crafter is a favorite
  const isFavorite = crafter ? checkIsFavorite(crafter.id, 'crafter') : false;
  
  const handleToggleFavorite = async () => {
    if (crafter) {
      await toggleFavorite(crafter.id, 'crafter');
    }
  };

  // Vetrina personalizzata (negozio personale Pro): configurazione dal backend.
  // Con ?anteprima=1 il proprietario vede la bozza salvata, anche non pubblicata.
  const [searchParams] = useSearchParams();
  const wantsPreview = searchParams.get('anteprima') === '1';
  const [shop, setShop] = useState(null);

  // Determine if crafter has Pro tier (for Premium badge display only)
  const isPro = crafter?.tier === 'pro';
  const isArtigiano = crafter?.crafter_type === 'professionista';
  // Advanced storefront is now available to all crafters
  const hasAdvancedStorefront = true;
  
  // Check if crafter has social links
  // Social links only available for PRO crafters
  // (durante la modalità lancio il backend restituisce can_customize=true per tutti gli artigiani)
  const hasSocialLinks = (crafter?.tier === 'pro' || shop?.can_customize) && crafter?.social_links && Object.values(crafter.social_links).some(v => v);

  // Section refs for scroll tracking
  const sectionRefs = {
    intro: useRef(null),
    prodotti: useRef(null),
    portfolio: useRef(null),
    collezioni: useRef(null),
    social: useRef(null),
    recensioni: useRef(null)
  };

  // Risolve slug -> id (retrocompat con vecchi URL UUID)
  useEffect(() => {
    let active = true;
    if (isUuid(routeKey)) {
      setId(routeKey);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('slug', routeKey)
        .maybeSingle();
      if (active && data) setId(data.id);
    })();
    return () => { active = false; };
  }, [routeKey]);

  useEffect(() => {
    if (id) {
      loadCrafterData();
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      if (wantsPreview && user?.id === id) {
        try {
          const mine = await fetchMyShop();
          if (active) {
            setShop({
              active: !!mine.can_customize,
              can_customize: !!mine.can_customize,
              branding: mine.branding,
              menu: mine.menu,
              featured_product_ids: mine.featured_product_ids,
              preview: true,
              is_published: !!mine.is_published,
            });
          }
          return;
        } catch (_) { /* si ricade sulla versione pubblica */ }
      }
      const pub = await fetchPublicShop(id);
      if (active) setShop(pub);
    })();
    return () => { active = false; };
  }, [id, wantsPreview, user?.id]);

  // SEO: title, description, canonical + redirect verso URL con slug
  useEffect(() => {
    if (!crafter) return;
    const name = crafter.storefront_title || crafter.full_name || 'Artigiano';
    setPageSeo({
      title: `${name} · Artigiano Made in Italy | Rarifex`,
      description: crafter.bio || crafter.storefront_motto || `Scopri le creazioni artigianali di ${name} su Rarifex.`,
      canonicalPath: `/artigiano/${crafter.slug || crafter.id}`,
    });
    if (crafter.slug && isUuid(routeKey) && crafter.id === routeKey) {
      navigate(`/artigiano/${crafter.slug}`, { replace: true });
    }
  }, [crafter, routeKey, navigate]);

  // Set initial selected media when crafter data loads
  useEffect(() => {
    if (crafter) {
      if (crafter.presentation_video) {
        setSelectedMedia(crafter.presentation_video);
        setMediaType('video');
      } else if (crafter.cover_images && crafter.cover_images.length > 0) {
        setSelectedMedia(crafter.cover_images[0]);
        setMediaType('image');
      } else if (crafter.cover_image) {
        setSelectedMedia(crafter.cover_image);
        setMediaType('image');
      }
    }
  }, [crafter]);

  // Extract unique categories from products
  useEffect(() => {
    if (products.length > 0) {
      const categories = [...new Set(products.map(p => {
        const path = p.customization_options?.categoryPath;
        return getChildCategoryName(path) || path?.sub || p.category || 'Altro';
      }))];
      setProductCategories(categories);
      setFilteredProducts(products);
    }
  }, [products]);

  // Filter and sort products
  useEffect(() => {
    let result = [...products];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.title.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(p => {
        const path = p.customization_options?.categoryPath;
        const cat = getChildCategoryName(path) || path?.sub || p.category || 'Altro';
        return cat === selectedCategory;
      });
    }
    
    // Sort with secondary comparator based on sortBy option
    let secondaryComparator;
    switch (sortBy) {
      case 'price-asc':
        secondaryComparator = (a, b) => a.base_price - b.base_price;
        break;
      case 'price-desc':
        secondaryComparator = (a, b) => b.base_price - a.base_price;
        break;
      case 'popular':
        secondaryComparator = (a, b) => (b.order_count || 0) - (a.order_count || 0);
        break;
      case 'newest':
      default:
        secondaryComparator = (a, b) => new Date(b.created_at) - new Date(a.created_at);
        break;
    }
    
    // Apply availability sorting (out-of-stock products at the end) with secondary sort
    result.sort(createAvailabilityComparator(secondaryComparator));
    
    setFilteredProducts(result);
  }, [products, searchQuery, sortBy, selectedCategory]);

  // Scroll tracking for active section
  useEffect(() => {
    const handleScroll = () => {
      // Check if menu should be sticky
      if (menuPlaceholderRef.current) {
        const placeholderTop = menuPlaceholderRef.current.getBoundingClientRect().top;
        setIsMenuSticky(placeholderTop <= 0);
      }
      
      // Find the section that is most visible at the top of the viewport
      const menuHeight = 70; // Height of sticky menu
      let currentSection = firstSectionRef.current;
      
      // Get all sections sorted by their position
      const sectionEntries = Object.entries(sectionRefs);
      
      for (const [section, ref] of sectionEntries) {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          // If the top of the section is at or above the menu bottom, 
          // and the bottom is still visible, this is the active section
          if (rect.top <= menuHeight + 50 && rect.bottom > menuHeight) {
            currentSection = section;
          }
        }
      }
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dopo il caricamento della configurazione: evidenzia la prima sezione del menu scelto
  useEffect(() => {
    if (window.scrollY < 100) setActiveSection(firstSectionRef.current);
  }, [shop, crafter, products.length, loading]);

  const scrollToSection = (sectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref?.current) {
      const offset = 70; // Account for sticky menu height
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const loadCrafterData = async () => {
    setLoading(true);
    try {
      const { data: crafterData, error: crafterError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (crafterError) throw crafterError;
      setCrafter(crafterData);

      const { data: productsData } = await supabase
        .from('products')
        .select('*')
        .eq('crafter_id', id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      setProducts(productsData || []);
      
      // Load review stats for products
      if (productsData && productsData.length > 0) {
        const productIds = productsData.map(p => p.id);
        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('product_id, rating')
          .in('product_id', productIds);
        
        if (reviewsData && reviewsData.length > 0) {
          const reviewMap = {};
          reviewsData.forEach(r => {
            if (!reviewMap[r.product_id]) {
              reviewMap[r.product_id] = { total: 0, count: 0 };
            }
            reviewMap[r.product_id].total += r.rating;
            reviewMap[r.product_id].count++;
          });
          
          // Calculate averages
          Object.keys(reviewMap).forEach(productId => {
            reviewMap[productId].average = reviewMap[productId].total / reviewMap[productId].count;
          });
          
          setProductReviews(reviewMap);
        }
      }

      const { data: portfolioData } = await supabase
        .from('portfolio_items')
        .select('*')
        .eq('crafter_id', id)
        .order('created_at', { ascending: false });

      setPortfolio(portfolioData || []);
      
      // Load completed orders count
      // For request-based orders (status: delivered or completed)
      const { data: requestOrdersData } = await supabase
        .from('orders')
        .select('id, status')
        .eq('crafter_id', id)
        .in('status', ['delivered', 'completed']);
      
      let completedCount = requestOrdersData?.length || 0;
      
      // For cart-based orders - need to check items array
      const { data: cartOrdersData } = await supabase
        .from('orders')
        .select('id, items, status')
        .eq('order_type', 'cart')
        .in('status', ['delivered', 'completed']);
      
      if (cartOrdersData) {
        const crafterCartOrders = cartOrdersData.filter(order => {
          if (!order.items || !Array.isArray(order.items)) return false;
          return order.items.some(item => item.crafter?.id === id);
        });
        completedCount += crafterCartOrders.length;
      }
      
      // Fallback: check all orders if initial queries return 0
      if (completedCount === 0) {
        const { data: allOrders } = await supabase
          .from('orders')
          .select('id, items, status, crafter_id, order_type');
        
        if (allOrders) {
          completedCount = allOrders.filter(order => {
            const isCompleted = ['delivered', 'completed', 'shipped'].includes(order.status);
            if (!isCompleted) return false;
            
            if (order.crafter_id === id) return true;
            
            if (order.items && Array.isArray(order.items)) {
              return order.items.some(item => item.crafter?.id === id);
            }
            
            return false;
          }).length;
        }
      }
      
      setCompletedOrdersCount(completedCount);
      
      // Load reviews and calculate average rating + sub-ratings
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('rating, rating_quality, rating_communication, rating_shipping, rating_packaging, rating_value')
        .eq('crafter_id', id);
      
      if (!reviewsError && reviewsData && reviewsData.length > 0) {
        const totalRating = reviewsData.reduce((sum, r) => sum + r.rating, 0);
        const avgRating = totalRating / reviewsData.length;
        
        // Calculate sub-rating averages
        const subRatingAverages = {};
        const subRatingKeys = ['rating_quality', 'rating_communication', 'rating_shipping', 'rating_packaging', 'rating_value'];
        subRatingKeys.forEach(key => {
          const values = reviewsData.filter(r => r[key]).map(r => r[key]);
          if (values.length > 0) {
            subRatingAverages[key] = values.reduce((a, b) => a + b, 0) / values.length;
          }
        });
        
        setReviewStats({
          averageRating: avgRating,
          reviewCount: reviewsData.length,
          subRatingAverages
        });
      }

    } catch (error) {
      console.error('Error loading crafter:', error);
      toast.error('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async () => {
    if (!user) {
      toast.error('Accedi per contattare l\'artigiano');
      navigate('/login');
      return;
    }
    
    if (user.id === crafter.id) {
      toast.error('Non puoi avviare una chat con te stesso');
      return;
    }

    setStartingChat(true);
    try {
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant_1.eq.${user.id},participant_2.eq.${crafter.id}),and(participant_1.eq.${crafter.id},participant_2.eq.${user.id})`)
        .single();

      if (existingConv) {
        navigate(`/messages?conversation=${existingConv.id}`);
      } else {
        const { data: newConv, error } = await supabase
          .from('conversations')
          .insert({
            participant_1: user.id,
            participant_2: crafter.id
          })
          .select()
          .single();

        if (error) throw error;
        navigate(`/messages?conversation=${newConv.id}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Errore nell\'avvio della chat');
    } finally {
      setStartingChat(false);
    }
  };


  // State for video playback (shows thumbnail until user clicks play)
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Link video (YouTube/Vimeo in tutti i formati): vedi utils/video.js
  const getVideoEmbedUrl = (url) => parseVideo(url)?.embedUrl || url;
  const getYouTubeVideoId = (url) => {
    const v = parseVideo(url);
    return v?.provider === 'youtube' ? v.id : null;
  };
  const getYouTubeThumbnail = (url) => parseVideo(url)?.thumbnailHd || null;
  const isVideoUrl = (url) => !!parseVideo(url) || /\.(mp4|webm|ogg)$/i.test(url || '');
  const isYouTubeUrl = (url) => parseVideo(url)?.provider === 'youtube';

  // Build media gallery items
  const getMediaItems = () => {
    const items = [];
    if (crafter?.presentation_video) {
      items.push({ type: 'video', url: crafter.presentation_video, label: 'Video' });
    }
    if (crafter?.cover_images && crafter.cover_images.length > 0) {
      crafter.cover_images.forEach((img, idx) => {
        items.push({ type: 'image', url: img, label: `Foto ${idx + 1}` });
      });
    } else if (crafter?.cover_image) {
      items.push({ type: 'image', url: crafter.cover_image, label: 'Foto' });
    }
    return items;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shop-primary"></div>
      </div>
    );
  }

  if (!crafter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Artigiano non trovato</h2>
          <Link to="/crafters">
            <Button className="bg-shop-primary text-white">Esplora Artigiani</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (crafter.deleted_at || crafter.storefront_disabled) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="storefront-unavailable">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Vetrina non disponibile</h2>
          <p className="text-slate-500 mb-4">Questa vetrina è stata temporaneamente disattivata dall'artigiano.</p>
          <Link to="/crafters">
            <Button className="bg-shop-primary text-white">Esplora Artigiani</Button>
          </Link>
        </div>
      </div>
    );
  }

  const mediaItems = getMediaItems();
  const hasMediaGallery = mediaItems.length > 0;
  // Solo le certificazioni Made in Italy dell'elenco predefinito (scelte nel profilo)
  const knownCertifications = (crafter.certifications || []).filter((c) => CERTIFICATIONS[c]);
  const hasCertifications = knownCertifications.length > 0;
  const hasPortfolio = portfolio && portfolio.length > 0;
  
  // Get the storefront section name from crafter preferences
  const sectionName = crafter.storefront_section_name || 'La Bottega';

  // Vetrina personalizzata attiva? Altrimenti modello standard (colori Rarifex, layout classico)
  const shopActive = !!shop?.active;
  // "Su misura" disattivato nel negozio = l'artigiano non accetta richieste su misura dirette
  const acceptsCustom = !shopActive || normalizeMenu(shop.menu).some((m) => m.key === 'su_misura' && m.enabled);
  const branding = shopActive ? shop.branding : null;
  const lc = layoutClasses(branding?.layout);
  const themeStyle = shopThemeStyle(branding);
  // Collezioni della vetrina avanzata (visibili e con almeno una creazione attiva)
  const shopCollections = shopActive
    ? (shop.collections || [])
        .filter((c) => c.visible !== false)
        .map((c) => {
          const items = (c.product_ids || []).map((pid) => products.find((p) => p.id === pid)).filter(Boolean);
          return { ...c, items, cover: products.find((p) => p.id === c.cover_product_id) || items[0] };
        })
        .filter((c) => c.items.length > 0)
    : [];
  const sectionAvailable = {
    intro: true,
    prodotti: true,
    collezioni: shopCollections.length > 0,
    portfolio: hasPortfolio,
    social: hasSocialLinks,
    recensioni: true,
  };
  const MENU_ICONS = { laboratorio: FileText, prodotti: Package, collezioni: Layers, su_misura: Sparkles, portfolio: Images, social: Share2, recensioni: Star };

  // Build menu items dynamically - include "Seguimi" only if crafter has social links, exclude Portfolio if empty
  const menuItems = shopActive
    ? normalizeMenu(shop.menu)
        .filter((m) => m.enabled)
        .map((m) => {
          const def = SHOP_MENU_ITEMS[m.key];
          if (m.key === 'su_misura') {
            return { id: 'su_misura', label: def.label, icon: MENU_ICONS[m.key], action: () => setShowDirectRequestDialog(true) };
          }
          return { id: def.section, label: m.key === 'laboratorio' ? sectionName : def.label, icon: MENU_ICONS[m.key] };
        })
        .filter((item) => item.action || sectionAvailable[item.id])
    : [
        { id: 'intro', label: sectionName, icon: FileText },
        { id: 'prodotti', label: 'Prodotti e Creazioni', icon: Package },
        ...(hasPortfolio ? [{ id: 'portfolio', label: 'Portfolio', icon: Images }] : []),
        ...(hasSocialLinks ? [{ id: 'social', label: 'Seguimi', icon: Share2 }] : []),
        { id: 'recensioni', label: 'Recensioni', icon: Star }
      ];

  // Ordine delle sezioni nella pagina: segue il menu (le voci disattivate nascondono la sezione)
  const orderedSectionIds = menuItems.filter((item) => !item.action).map((item) => item.id);
  firstSectionRef.current = orderedSectionIds[0] || 'intro';

  // Prodotti in evidenza scelti dall'artigiano (solo vetrina personalizzata)
  const featuredProducts = shopActive
    ? (shop.featured_product_ids || []).map((pid) => products.find((p) => p.id === pid)).filter(Boolean)
    : [];

  const handleMenuClick = (item) => (item.action ? item.action() : scrollToSection(item.id));

  const renderProductCard = (product, index) => {
                      const mainImgIndex = product.customization_options?.main_image_index || 0;
                      const mainImage = optimizeImageUrl(product.images?.[mainImgIndex] || product.images?.[0], 500);
                      
                      return (
                        <motion.div
                          key={product.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                        >
                          <Link to={productUrl(product)}>
                            <Card className={lc.productCard}>
                              <div className={`${lc.productImage} bg-slate-100 overflow-hidden relative flex-shrink-0`}>
                                {mainImage ? (
                                  <img 
                                    src={mainImage} 
                                    alt={product.title}
                                    className={`w-full h-full object-cover ${lc.imageHover} transition-all duration-500`}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Package className="w-12 h-12 text-slate-300" />
                                  </div>
                                )}
                                {product.category && (
                                  <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700 text-xs">
                                    {getChildCategoryName(product.customization_options?.categoryPath) ||
                                     product.customization_options?.categoryPath?.sub ||
                                     product.category}
                                  </Badge>
                                )}
                                {/* Availability + customization badges - bottom left */}
                                {(() => {
                                  const { isAvailable, isOnDemand } = getProductAvailability(product);
                                  const hasCustomizations = product.customization_options?.customizations?.length > 0;
                                  if (!isAvailable) {
                                    return (
                                      <Badge className="absolute bottom-2 left-2 bg-red-500 text-white text-xs">
                                        Esaurito
                                      </Badge>
                                    );
                                  }
                                  if (hasCustomizations) {
                                    return (
                                      <Badge className="absolute bottom-2 left-2 bg-shop-accent text-white text-xs">
                                        Personalizzabile
                                      </Badge>
                                    );
                                  }
                                  if (isOnDemand) {
                                    return (
                                      <Badge className="absolute bottom-2 left-2 bg-shop-primary text-white text-xs">
                                        Su commissione
                                      </Badge>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <div className="p-3 flex flex-col flex-1">
                                <h3 className="font-semibold text-slate-800 mb-1 line-clamp-1 group-hover:text-shop-primary transition-colors text-sm">{product.title}</h3>
                                
                                {/* Review preview - min-height ensures consistent card height */}
                                <div className="min-h-[20px] mb-2">
                                  {productReviews[product.id] && productReviews[product.id].count > 0 && (
                                    <div className="flex items-center gap-1">
                                      <Star className="w-3.5 h-3.5 text-shop-accent fill-shop-accent" />
                                      <span className="text-xs font-medium text-slate-700">
                                        {productReviews[product.id].average.toFixed(1)}
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        ({productReviews[product.id].count})
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center justify-between mt-auto">
                                  {(() => {
                                    const crafterDiscount = product.customization_options?.crafterDiscount || 0;
                                    const launchModeActive = isLaunchModeActive();
                                    const hasDiscount = crafterDiscount > 0 || launchModeActive;
                                    const originalPrice = calculateFullPrice(product.base_price);
                                    const finalPrice = calculateFinalPrice(product.base_price, crafterDiscount);
                                    const totalDiscountPercent = getTotalDiscountPercentage(crafterDiscount);
                                    
                                    if (hasDiscount && originalPrice !== finalPrice) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <div className="flex flex-col">
                                            <span className="text-xs text-slate-400 line-through">
                                              €{originalPrice.toFixed(2)}
                                            </span>
                                            <span className="text-lg font-bold text-shop-primary">
                                              €{finalPrice.toFixed(2)}
                                            </span>
                                          </div>
                                          <Badge className={`${crafterDiscount > 0 && launchModeActive ? 'bg-gradient-to-r from-orange-500 to-green-500' : crafterDiscount > 0 ? 'bg-orange-500' : 'bg-green-500'} text-white text-[10px] px-1.5 py-0.5`}>
                                            -{totalDiscountPercent}%
                                          </Badge>
                                        </div>
                                      );
                                    }
                                    return (
                                      <span className="text-lg font-bold text-shop-primary">
                                        €{finalPrice.toFixed(2)}
                                      </span>
                                    );
                                  })()}
                                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-shop-primary group-hover:translate-x-1 transition-all" />
                                </div>
                              </div>
                            </Card>
                          </Link>
                        </motion.div>
                      );
  };

  const introSection = (
        <section ref={sectionRefs.intro} id="intro">
          {/* Motto as section header */}
          {crafter.storefront_motto && (
            <div className="text-center mb-8">
              <p className="text-xl md:text-2xl font-light text-slate-600 italic">
                "{crafter.storefront_motto}"
              </p>
            </div>
          )}
          
          {/* Gallery - shown only if has media */}
          {hasMediaGallery && (
            <div className="mb-10">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Main Viewer */}
                <div className="flex-1 min-h-[280px] lg:min-h-[380px]">
                  <div className="relative w-full h-full bg-slate-900 rounded-2xl overflow-hidden shadow-lg">
                    {mediaType === 'video' && selectedMedia ? (
                      // YouTube video - show clean thumbnail with custom play button until clicked
                      isYouTubeUrl(selectedMedia) ? (
                        videoPlaying ? (
                          <iframe
                            src={getVideoEmbedUrl(selectedMedia)}
                            title="Video di presentazione"
                            className="w-full h-full min-h-[280px] lg:min-h-[380px]"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <button 
                            onClick={() => setVideoPlaying(true)}
                            className="relative w-full h-full min-h-[280px] lg:min-h-[380px] group cursor-pointer"
                          >
                            {/* YouTube Thumbnail */}
                            <img 
                              src={getYouTubeThumbnail(selectedMedia)}
                              alt="Anteprima video"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Fallback to hqdefault if maxresdefault doesn't exist
                                e.target.src = `https://img.youtube.com/vi/${getYouTubeVideoId(selectedMedia)}/hqdefault.jpg`;
                              }}
                            />
                            {/* Gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                            {/* Custom Play Button */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-20 h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-white transition-all duration-300">
                                <Play className="w-8 h-8 text-shop-primary ml-1" fill="currentColor" />
                              </div>
                            </div>
                            {/* "Guarda il video" label */}
                            <div className="absolute bottom-4 left-4 text-white text-sm font-medium opacity-80">
                              Guarda il video
                            </div>
                          </button>
                        )
                      ) : isVideoUrl(selectedMedia) && selectedMedia.includes('vimeo') ? (
                        <iframe
                          src={getVideoEmbedUrl(selectedMedia)}
                          title="Video di presentazione"
                          className="w-full h-full min-h-[280px] lg:min-h-[380px]"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <video 
                          src={optimizeImageUrl(selectedMedia, 1200)} 
                          controls 
                          className="w-full h-full min-h-[280px] lg:min-h-[380px] object-contain"
                        />
                      )
                    ) : selectedMedia ? (
                      <img 
                        src={optimizeImageUrl(selectedMedia, 1200)} 
                        alt="Immagine selezionata"
                        className="w-full h-full min-h-[280px] lg:min-h-[380px] object-contain"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[280px] lg:min-h-[380px] flex items-center justify-center text-slate-400">
                        <ImageIcon className="w-16 h-16" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Thumbnails */}
                {mediaItems.length > 1 && (
                  <div className="lg:w-32 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto lg:max-h-[380px] pb-2 lg:pb-0">
                    {mediaItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedMedia(item.url);
                          setMediaType(item.type);
                          setVideoPlaying(false); // Reset video playback state when switching media
                        }}
                        className={`relative flex-shrink-0 w-20 h-20 lg:w-full lg:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                          selectedMedia === item.url 
                            ? 'border-shop-primary ring-2 ring-shop-primary/30' 
                            : 'border-slate-200 hover:border-shop-primary/50'
                        }`}
                      >
                        {item.type === 'video' ? (
                          isYouTubeUrl(item.url) ? (
                            // Show YouTube thumbnail for video thumbnails too
                            <div className="relative w-full h-full">
                              <img 
                                src={`https://img.youtube.com/vi/${getYouTubeVideoId(item.url)}/mqdefault.jpg`}
                                alt={item.label}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                                  <Play className="w-4 h-4 text-shop-primary ml-0.5" fill="currentColor" />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <Play className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          )
                        ) : (
                          <img 
                            src={item.url} 
                            alt={item.label}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Description text */}
          <div className="max-w-3xl mx-auto">
            {crafter.bio ? (
              <p className="text-slate-600 whitespace-pre-line leading-relaxed text-base md:text-lg">
                {crafter.bio}
              </p>
            ) : (
              <p className="text-slate-400 italic text-center">Nessuna descrizione disponibile</p>
            )}
            
            {/* Materiali e tecniche */}
            {Array.isArray(crafter.materials) && crafter.materials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="storefront-materials-chips">
                {crafter.materials.map((m) => (
                  <span key={m} className="px-3 py-1 rounded-full bg-shop-primary/10 text-shop-primary text-sm">{m}</span>
                ))}
              </div>
            )}

            {/* La Nostra Storia - integrated with small separator */}
            {crafter.workshop_story && (
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  La Nostra Storia
                </h3>
                <p className="text-slate-600 whitespace-pre-line leading-relaxed">
                  {crafter.workshop_story}
                </p>
                {crafter.founded_year && (
                  <div className="mt-6 inline-flex items-center gap-2 bg-shop-primary/10 px-4 py-2 rounded-full">
                    <Calendar className="w-4 h-4 text-shop-primary" />
                    <span className="font-medium text-slate-700">Attivi dal {crafter.founded_year}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
  );
  const productsSection = (
        <section ref={sectionRefs.prodotti} id="prodotti">
          <h2 className={`${lc.heading} mb-6`}>
            Prodotti e Creazioni
            <span className="ml-2 text-base font-normal text-slate-500">({products.length})</span>
          </h2>
            
            {featuredProducts.length > 0 && (
              <div className="mb-10" data-testid="shop-featured">
                <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-shop-primary mb-4">
                  <Sparkles className="w-4 h-4" />
                  In evidenza
                </h3>
                <div className={lc.productGrid}>
                  {featuredProducts.map(renderProductCard)}
                </div>
              </div>
            )}

            <div className="flex">
              {/* Sidebar Catalogo */}
              {productCategories.length > 0 && (
                <div className="hidden md:block w-56 border-r bg-slate-50/50 p-4">
                  <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    Catalogo
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === 'all'
                          ? 'bg-shop-primary text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Tutte le creazioni
                      <span className="float-right opacity-70">{products.length}</span>
                    </button>
                    {productCategories.map((cat) => {
                      const count = products.filter(p => {
                        const path = p.customization_options?.categoryPath;
                        return (getChildCategoryName(path) || path?.sub || p.category || 'Altro') === cat;
                      }).length;
                      return (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedCategory === cat
                              ? 'bg-shop-primary text-white'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          {cat}
                          <span className="float-right opacity-70">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Products Grid */}
              <div className="flex-1 p-4">
                {/* Search and Sort Bar */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca prodotti..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Mobile Category Select */}
                  {productCategories.length > 0 && (
                    <Select value={selectedCategory} onValueChange={setSelectedCategory} className="md:hidden">
                      <SelectTrigger className="w-full sm:w-40">
                        <Folder className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte</SelectItem>
                        {productCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full sm:w-48">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Ordina per" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Più recenti</SelectItem>
                      <SelectItem value="price-asc">Prezzo: basso-alto</SelectItem>
                      <SelectItem value="price-desc">Prezzo: alto-basso</SelectItem>
                      <SelectItem value="popular">Più popolari</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Results count */}
                {searchQuery || selectedCategory !== 'all' ? (
                  <p className="text-sm text-slate-500 mb-4">
                    {filteredProducts.length} risultat{filteredProducts.length === 1 ? 'o' : 'i'} trovati
                    {searchQuery && <span> per "{searchQuery}"</span>}
                    {selectedCategory !== 'all' && <span> in {selectedCategory}</span>}
                  </p>
                ) : null}

                {filteredProducts.length > 0 ? (
                  <div className={lc.productGrid}>
                    {filteredProducts.map(renderProductCard)}
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">
                      {searchQuery || selectedCategory !== 'all' 
                        ? 'Nessun prodotto corrisponde ai filtri'
                        : 'Nessuna creazione disponibile'
                      }
                    </p>
                    {(searchQuery || selectedCategory !== 'all') && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('all');
                        }}
                      >
                        Rimuovi filtri
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
        </section>
  );
  // Portfolio - solo se ci sono lavori
  // Collezioni: ogni riquadro apre la collezione nel negozio della bottega
  const collectionsSection = shopCollections.length > 0 ? (
          <section ref={sectionRefs.collezioni} id="collezioni" data-testid="storefront-collections">
            <h2 className={`${lc.heading} mb-6`}>Collezioni</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shopCollections.map((c) => {
                const mi = c.cover?.customization_options?.main_image_index || 0;
                const src = optimizeImageUrl(c.cover?.images?.[mi] || c.cover?.images?.[0], 600);
                return (
                  <Link
                    key={c.id}
                    to={`/artigiano/${crafter.slug || slugFromRoute}/collezioni/${c.slug}?negozio=1${shop?.preview ? '&anteprima=1' : ''}`}
                    className="group relative block aspect-[4/3] rounded-xl overflow-hidden bg-slate-200 shadow-md"
                  >
                    {src && <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <span className="absolute inset-x-0 bottom-0 p-4 text-white">
                      <span className="text-xs uppercase tracking-widest text-white/75">{c.items.length} {c.items.length === 1 ? 'creazione' : 'creazioni'}</span>
                      <span className="block text-xl font-semibold">{c.title}</span>
                      {c.description && <span className="block text-sm text-white/85 line-clamp-1">{c.description}</span>}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
  ) : null;

  const portfolioSection = hasPortfolio ? (
          <section ref={sectionRefs.portfolio} id="portfolio">
            <h2 className={`${lc.heading} mb-6`}>
              Portfolio
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolio.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="overflow-hidden hover:shadow-xl transition-all group border-0 shadow-md">
                    <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                      {item.description && (
                        <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
  ) : null;
  // Social - solo se l'artigiano ha almeno un link (piano Pro o modalità lancio)
  const socialSection = hasSocialLinks ? (
          <section ref={sectionRefs.social} id="social">
            <h2 className={`${lc.heading} mb-6`}>
              Seguimi
            </h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {/* Website - shown first */}
              {crafter.social_links.website && (
                <a 
                  href={crafter.social_links.website.startsWith('http') ? crafter.social_links.website : `https://${crafter.social_links.website}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-shop-primary text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <Globe className="w-5 h-5" />
                  Sito Web
                </a>
              )}
              {crafter.social_links.instagram && (
                <a 
                  href={crafter.social_links.instagram.startsWith('http') ? crafter.social_links.instagram : `https://${crafter.social_links.instagram}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </a>
              )}
              {crafter.social_links.facebook && (
                <a 
                  href={crafter.social_links.facebook.startsWith('http') ? crafter.social_links.facebook : `https://${crafter.social_links.facebook}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
              )}
              {crafter.social_links.tiktok && (
                <a 
                  href={crafter.social_links.tiktok.startsWith('http') ? crafter.social_links.tiktok : `https://${crafter.social_links.tiktok}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                  TikTok
                </a>
              )}
              {crafter.social_links.youtube && (
                <a 
                  href={crafter.social_links.youtube.startsWith('http') ? crafter.social_links.youtube : `https://${crafter.social_links.youtube}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube
                </a>
              )}
              {crafter.social_links.pinterest && (
                <a 
                  href={crafter.social_links.pinterest.startsWith('http') ? crafter.social_links.pinterest : `https://${crafter.social_links.pinterest}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500 text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
                  </svg>
                  Pinterest
                </a>
              )}
              {crafter.social_links.linkedin && (
                <a 
                  href={crafter.social_links.linkedin.startsWith('http') ? crafter.social_links.linkedin : `https://${crafter.social_links.linkedin}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-700 text-white hover:opacity-90 transition-opacity shadow-md"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </a>
              )}
            </div>
          </section>
  ) : null;
  const reviewsSection = (
        <section ref={sectionRefs.recensioni} id="recensioni" className="mb-12">
          <h2 className={`${lc.heading} mb-6`}>
            Recensioni
            {crafter?.review_count > 0 && (
              <span className="ml-2 text-base font-normal text-slate-500">({crafter.review_count})</span>
            )}
          </h2>
          <ReviewsList crafterId={id} limit={20} />
        </section>
  );

  const sectionsById = {
    intro: introSection,
    prodotti: productsSection,
    collezioni: collectionsSection,
    portfolio: portfolioSection,
    social: socialSection,
    recensioni: reviewsSection,
  };
  const sectionSeparator = (
    <div className="my-12 flex items-center justify-center">
      <div className={`h-px bg-gradient-to-r from-transparent ${branding ? 'via-shop-primary/30' : 'via-slate-300'} to-transparent w-full max-w-md`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-shop-bg" style={themeStyle} data-testid="crafter-profile-page" data-shop-layout={branding?.layout || 'standard'}>
      {/* Anteprima per il proprietario (?anteprima=1) */}
      {shop?.preview && (
        <div className="bg-slate-900 text-white text-sm" data-testid="shop-preview-banner">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
            <span>
              {shop.can_customize
                ? `Anteprima della tua vetrina personalizzata${shop.is_published ? '' : ' — non ancora pubblicata: i clienti vedono il modello standard'}`
                : 'La vetrina personalizzata è inclusa nel piano Pro: i clienti vedono il modello standard'}
            </span>
            <Link to="/dashboard?tab=artigiano&section=storefront" className="underline underline-offset-2">Torna all'editor</Link>
          </div>
        </div>
      )}
      {/* Banner: artigiano in ferie */}
      {vacationStatus(crafter).active && (
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4" data-testid="storefront-vacation-banner">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">
                {(crafter.storefront_title || crafter.full_name || 'L\'artigiano')} è in ferie fino al {formatDateIt(vacationStatus(crafter).end)}
              </p>
              <p>Puoi vedere i prodotti dell'artigiano, ma puoi acquistarli solo al suo rientro. {vacationStatus(crafter).message ? ` ${vacationStatus(crafter).message}` : ''}</p>
            </div>
          </div>
        </div>
      )}
      {/* Banner: artigiano non ancora attivato per la vendita */}
      {!canSellLoading && crafterCanSell === false && (
        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4">
          <CrafterStripeStatusBanner
            variant="client"
            crafterName={crafter?.storefront_title || crafter?.full_name || 'Questo artigiano'}
          />
        </div>
      )}

      {/* Hero Cover */}
      <div className={`${lc.hero} ${lc.hideHeroImage ? 'bg-shop-primary/10' : 'bg-gradient-to-br from-shop-primary to-shop-primary-dark'}`}>
        {!lc.hideHeroImage && crafter.cover_image && (
          <img 
            src={optimizeImageUrl(crafter.cover_image, 1600)} 
            alt={crafter.storefront_title || crafter.full_name}
            className="w-full h-full object-cover"
          />
        )}
        {!lc.hideHeroImage && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />}
      </div>

      {/* Profile Header Card with Certifications */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 -mt-20 relative z-10">
        <Card className={`bg-white/95 backdrop-blur-sm ${lc.headerCard} rounded-2xl p-6 md:p-8`}>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-28 h-28 md:w-32 md:h-32 rounded-2xl border-4 border-white bg-white shadow-lg overflow-hidden flex-shrink-0 mx-auto md:mx-0"
            >
              {branding?.logo_url ? (
                <img src={optimizeImageUrl(branding.logo_url, 300)} alt={`Logo di ${crafter.storefront_title || crafter.full_name}`} className="w-full h-full object-contain p-2" data-testid="shop-logo" />
              ) : crafter.avatar_url ? (
                <img src={optimizeImageUrl(crafter.avatar_url, 200)} alt={crafter.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-shop-primary to-shop-primary-dark flex items-center justify-center">
                  <span className="text-4xl font-bold text-white">
                    {(crafter.storefront_title || crafter.full_name || 'A')[0].toUpperCase()}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                  {crafter.storefront_title || crafter.full_name}
                </h1>
                {crafter.rarifex_verified && <RarifexVerifiedBadge size="lg" />}
              </div>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
                <CrafterBadge 
                  crafterType={crafter.crafter_type || 'hobbista'} 
                  tier={crafter.tier || 'free'}
                  piva={crafter.partita_iva}
                  size="md"
                />
                {/* Certifications badges inline */}
                {hasCertifications && knownCertifications.slice(0, 3).map((cert, index) => (
                  <Badge 
                    key={index}
                    className="bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 border border-amber-200 text-xs"
                  >
                    <Award className="w-3 h-3 mr-1" />
                    {CERTIFICATIONS[cert].label}
                  </Badge>
                ))}
                {hasCertifications && knownCertifications.length > 3 && (
                  <Badge variant="outline" className="text-xs text-slate-500">
                    +{knownCertifications.length - 3} altri
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-slate-500 mb-4">
                {crafter.categories?.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    {crafter.categories.map((cat, i) => (
                      <Badge key={i} variant="outline" className="bg-white">{cat}</Badge>
                    ))}
                  </div>
                )}
                {crafter.city && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-shop-primary" />
                    <span>{crafter.city}{crafter.region ? `, ${crafter.region}` : ''}</span>
                  </div>
                )}
                {crafter.founded_year && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-shop-primary" />
                    <span>Dal {crafter.founded_year}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center md:justify-start gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-shop-primary">{products.length}</p>
                  <p className="text-xs text-slate-500">Creazioni</p>
                </div>
                <Separator orientation="vertical" className="h-10" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-shop-primary">{completedOrdersCount}</p>
                  <p className="text-xs text-slate-500">Prodotti venduti</p>
                </div>
                <Separator orientation="vertical" className="h-10" />
                <div className="text-center flex items-center gap-1">
                  <Star className="w-5 h-5 text-shop-accent fill-shop-accent" />
                  <p className="text-2xl font-bold text-slate-800">
                    {reviewStats.averageRating > 0 ? reviewStats.averageRating.toFixed(1) : '0.0'}
                  </p>
                  {reviewStats.reviewCount > 0 && (
                    <span className="text-xs text-slate-500 ml-1">({reviewStats.reviewCount})</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 items-center md:items-end">
              {/* Vetrina avanzata: ingresso nel negozio personale (modalità negozio) */}
              {shopActive && (
                <Link
                  to={`/artigiano/${crafter.slug || slugFromRoute}?negozio=1${shop?.preview ? '&anteprima=1' : ''}`}
                  className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 h-11 rounded-md bg-shop-primary text-white font-medium hover:bg-shop-primary-dark transition-colors"
                  data-testid="enter-shop-btn"
                >
                  <Store className="w-5 h-5" />
                  Entra nel negozio
                </Link>
              )}
              {/* Richiesta su misura (nascosta se l'artigiano non accetta lavori su commissione) */}
              {acceptsCustom && (
                <>
                  <Button 
                    onClick={() => setShowDirectRequestDialog(true)}
                    className="bg-shop-accent text-white hover:bg-shop-accent/90 w-full md:w-auto px-8"
                    size="lg"
                    data-testid="direct-request-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Richiedi creazione su misura
                  </Button>
                  <p className="text-xs text-slate-500 text-center md:text-right max-w-[200px]">
                    Descrivi la tua idea e ricevi una proposta personalizzata
                  </p>
                </>
              )}
              
              <Button 
                onClick={handleStartChat}
                disabled={startingChat}
                variant="outline"
                className="border-shop-primary text-shop-primary hover:bg-shop-primary/10 w-full md:w-[140px] mt-2"
                size="lg"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Contatta
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className={`w-full md:w-[140px] ${isFavorite ? 'border-red-300 bg-red-50 text-red-600' : ''}`}
                onClick={handleToggleFavorite}
                data-testid="favorite-crafter-btn"
              >
                <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                {isFavorite ? 'Salvato' : 'Salva'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Sticky Navigation Menu */}
        <div ref={menuPlaceholderRef} className="mt-6">
          {/* Placeholder to maintain spacing when menu becomes fixed */}
          {isMenuSticky && <div className="h-[56px] md:h-[56px]" />}
        </div>
        
        <div 
          ref={menuRef}
          className={`z-30 bg-white transition-all duration-300 ${
            isMenuSticky 
              ? 'fixed top-0 left-0 right-0 shadow-lg border-b border-slate-200' 
              : 'rounded-xl shadow-md border border-slate-100'
          }`}
        >
          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center overflow-x-auto scrollbar-hide ${isMenuSticky ? 'max-w-7xl mx-auto' : ''}`}>
            {/* Show crafter info when sticky */}
            {isMenuSticky && (
              <div className="flex items-center gap-3 px-4 py-2 border-r border-slate-200 flex-shrink-0">
                <img 
                  src={crafter?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${crafter?.full_name}`} 
                  alt={crafter?.full_name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="font-semibold text-slate-800 text-sm truncate max-w-[150px]">
                  {crafter?.shop_name || crafter?.full_name}
                </span>
              </div>
            )}
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item)}
                  className="relative flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-all duration-300 group"
                >
                  {/* Background glow effect */}
                  <span className={`absolute inset-0 transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-t from-shop-primary/10 via-shop-primary/5 to-transparent' 
                      : 'bg-transparent group-hover:bg-gradient-to-t group-hover:from-slate-100 group-hover:to-transparent'
                  }`} />
                  
                  {/* Animated bottom indicator */}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[3px] rounded-full transition-all duration-300 ease-out ${
                    isActive 
                      ? 'w-full bg-gradient-to-r from-shop-primary via-shop-primary/70 to-shop-primary' 
                      : 'w-0 bg-shop-primary group-hover:w-1/2'
                  }`} />
                  
                  {/* Icon with animation */}
                  <span className={`relative z-10 transition-all duration-300 ${
                    isActive 
                      ? 'text-shop-primary scale-110' 
                      : 'text-slate-400 group-hover:text-shop-primary group-hover:scale-110 group-hover:rotate-3'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  
                  {/* Label */}
                  <span className={`relative z-10 transition-all duration-300 ${
                    isActive 
                      ? 'text-shop-primary font-semibold' 
                      : 'text-slate-600 group-hover:text-shop-primary'
                  }`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Mobile/Tablet Navigation - Icon pills */}
          <nav className={`md:hidden flex items-center justify-around py-2 px-2 ${isMenuSticky ? '' : ''}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuClick(item)}
                  className={`relative flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-shop-primary text-white shadow-lg scale-105' 
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                  <span className={`text-[10px] font-medium leading-none ${isActive ? 'text-white' : 'text-slate-500'}`}>
                    {item.label.split(' ')[0]}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="mt-8">
          {orderedSectionIds.map((sid, idx) => (
            <React.Fragment key={sid}>
              {idx > 0 && sectionSeparator}
              {sectionsById[sid]}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Richiesta su misura diretta all'artigiano */}
      <DirectRequestDialog crafter={crafter} open={showDirectRequestDialog} onOpenChange={setShowDirectRequestDialog} />
    </div>
  );
}
